import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import apiClient from '../../services/apiClient'
import { hasConsultantRole } from '../../utils/roles'
import { toast } from 'react-hot-toast'
import {
	WizardStatus,
	WizardStepId,
	WizardStepStatus,
} from '../../../../shared/types'
import { calculateWeightedPhaseProgress } from '../../../../shared/phase-progress'
import { WizardComponentProps } from '../../types'

// Step Components
import CredentialForm from '../credentials/CredentialForm'
import CustomerSharePointCredentialsForm from '../customers/CustomerCredentialsForm'
import MetadataViewer from '../metadata/MetadataViewer'
import PhaseEntities from '../phases/PhaseEntities'
import FileUploader from '../files/FileUploader'
import MappingCanvasV3 from '../mapping/MappingCanvasV3'
import ValidationPanelV3 from '../validation/ValidationPanelV3'
import ExportConfiguration from '../export/ExportConfiguration'

type StepMeta = {
	description: string
	optional?: boolean
}

const STEP_ORDER: WizardStepId[] = [
	'welcome',
	'customer',
	'phase',
	'credentials',
	'sharepoint',
	'entities',
	'metadata',
	'files',
	'mapping',
	'validation',
	'export',
	'review',
]

const STEP_META: Record<WizardStepId, StepMeta> = {
	welcome: {
		description: 'Review the migration flow and start the wizard',
	},
	customer: {
		description: 'Select the customer for this migration',
	},
	phase: {
		description: 'Create or choose a migration phase',
	},
	credentials: {
		description: 'Store and test SuccessFactors credentials',
	},
	sharepoint: {
		description: 'Optional SharePoint connection for file browsing',
		optional: true,
	},
	metadata: {
		description: 'Fetch SuccessFactors metadata for selected entities',
	},
	entities: {
		description: 'Select which entities are included in this phase',
	},
	files: {
		description: 'Choose source files for mapping and export',
	},
	mapping: {
		description: 'Create field mappings for required entities',
	},
	validation: {
		description: 'Run validation checks for mappings and data',
	},
	export: {
		description: 'Generate export files for SuccessFactors',
	},
	review: {
		description: 'Confirm readiness and mark migration complete',
	},
}

const isDebugLogging =
	(import.meta.env.VITE_LOGGING || '').toLowerCase() === 'debug'

function logWizardAction(
	message: string,
	details?: Record<string, any>
): void {
	if (!isDebugLogging) return
	if (details) {
		console.log(`[WIZARD_UI] ${message}`, details)
	} else {
		console.log(`[WIZARD_UI] ${message}`)
	}
}

function WizardStepItem({
	step,
	isSelected,
	onSelect,
}: {
	step: WizardStepStatus
	isSelected: boolean
	onSelect: (stepId: WizardStepId) => void
}) {
	function handleSelect() {
		onSelect(step.stepId)
	}

	const statusStyles: Record<WizardStepStatus['status'], string> = {
		completed: 'text-green-700 bg-green-50 border-green-200',
		skipped: 'text-yellow-700 bg-yellow-50 border-yellow-200',
		available: 'text-blue-700 bg-blue-50 border-blue-200',
		locked: 'text-gray-400 bg-gray-50 border-gray-200',
	}
	const buttonClassName = [
		'w-full text-left p-3 rounded-md border transition-colors',
		isSelected
			? 'border-indigo-500 bg-indigo-50 shadow-sm'
			: 'border-gray-200 hover:border-indigo-200',
	].join(' ')
	const statusClassName = [
		'text-xs px-2 py-1 rounded',
		statusStyles[step.status],
	].join(' ')

	return (
		<button
			type="button"
			onClick={handleSelect}
			className={buttonClassName}
		>
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium text-gray-900">
					{step.label}
				</span>
				<span className={statusClassName}>
					{step.status}
				</span>
			</div>
		</button>
	)
}

function WizardPage() {
	const { customerId, phaseId } = useParams<{
		customerId: string
		phaseId: string
	}>()
	const { instance } = useMsal()
	const [currentStepId, setCurrentStepId] =
		useState<WizardStepId | null>(null)

	const isConsultant = useMemo(() => hasConsultantRole(instance), [instance])

	const wizardQuery = useQuery({
		queryKey: ['wizard-status', customerId, phaseId],
		queryFn: () =>
			apiClient.getWizardStatus(customerId!, phaseId!),
		enabled: Boolean(customerId && phaseId),
	})

	const wizardStatus = wizardQuery.data as WizardStatus | undefined

	const stepList = useMemo(() => {
		if (!wizardStatus) return []
		return STEP_ORDER
			.map((stepId) => {
				const step = wizardStatus.steps.find(
					(item) => item.stepId === stepId
				)
				return step
			})
			.filter(Boolean)
			.filter((step) => {
				if (!step) return false
				if (!isConsultant && step.access === 'consultant') {
					return false
				}
				return true
			}) as WizardStepStatus[]
	}, [wizardStatus, isConsultant])

	const currentStep = useMemo(() => {
		if (!currentStepId) return undefined
		return stepList.find((step) => step.stepId === currentStepId)
	}, [stepList, currentStepId])

	useEffect(() => {
		if (!wizardStatus || stepList.length === 0) return
		if (currentStepId) return // Don't redirect if we already have a step selected (even if manually)

		const nextStep =
			stepList.find(
				(step) => step.status === 'available'
			) || stepList[0]

		if (nextStep) {
			setCurrentStepId(nextStep.stepId)
		}
	}, [wizardStatus, stepList, currentStepId])

	const validateMutation = useMutation({
		mutationFn: () =>
			apiClient.validateWizardStep(
				customerId!,
				phaseId!,
				currentStepId as WizardStepId
			),
		onSuccess: (data) => {
			logWizardAction('validate_success', {
				stepId: currentStepId,
				data,
			})
			wizardQuery.refetch()
		},
		onError: (error) => {
			logWizardAction('validate_error', {
				stepId: currentStepId,
				error,
			})
		},
	})

	const completeMutation = useMutation({
		mutationFn: (options?: { skip?: boolean }) =>
			apiClient.completeWizardStep(
				customerId!,
				phaseId!,
				currentStepId as WizardStepId,
				options
			),
		onSuccess: (data) => {
			logWizardAction('complete_success', {
				stepId: currentStepId,
				data,
			})
			wizardQuery.refetch()
		},
		onError: (error) => {
			logWizardAction('complete_error', {
				stepId: currentStepId,
				error,
			})
		},
	})

	const handleSelectStep = useCallback((stepId: WizardStepId) => {
		logWizardAction('step_selected', { stepId })
		setCurrentStepId(stepId)
	}, [])

	const handleValidateStep = useCallback(() => {
		if (!currentStep) return
		validateMutation.mutate()
	}, [currentStep, validateMutation])

	const handleCompleteStep = useCallback(() => {
		if (!currentStep) return
		completeMutation.mutate({})
	}, [currentStep, completeMutation])

	const handleSkipStep = useCallback(() => {
		if (!currentStep) return
		completeMutation.mutate({ skip: true })
	}, [currentStep, completeMutation])

	const handleNextStep = useCallback(() => {
		const index = stepList.findIndex(
			(step) =>
				step.stepId === currentStepId
		)
		if (index < 0 || index >= stepList.length - 1) return
		setCurrentStepId(stepList[index + 1].stepId)
	}, [stepList, currentStepId])

	const handlePreviousStep = useCallback(() => {
		const index = stepList.findIndex(
			(step) =>
				step.stepId === currentStepId
		)
		if (index <= 0) return
		setCurrentStepId(stepList[index - 1].stepId)
	}, [stepList, currentStepId])

	const completionStats = useMemo(() => {
		const completed = stepList.filter(
			(step) =>
				step.status === 'completed'
		).length
		const weightedProgress = calculateWeightedPhaseProgress(
			wizardStatus?.steps || []
		)
		return {
			completed,
			total: stepList.length,
			weightedPercent: weightedProgress.percentage,
		}
	}, [stepList, wizardStatus])

	// ⚠️ Must be defined here (before early returns) to obey Rules of Hooks.
	// It references handlePreviousStep which is already defined above.
	const handleChildFailure = useCallback((error: string) => {
		toast.error(error);
		handlePreviousStep();
	}, [handlePreviousStep]);

	const spinnerClassName =
		'loading-spinner w-8 h-8 border-4 ' +
		'border-blue-600 border-t-transparent rounded-full'
	const layoutClassName =
		'grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6'
	const warningClassName =
		'mt-4 p-4 bg-yellow-50 border border-yellow-200 ' +
		'text-yellow-800 rounded-md'
	const lockedClassName =
		'mt-4 p-4 bg-gray-50 border border-gray-200 ' +
		'text-gray-600 rounded-md'

	if (wizardQuery.isLoading || (wizardStatus && !currentStepId)) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className={spinnerClassName}></div>
			</div>
		)
	}

	if (!wizardStatus || !currentStep) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<p className="text-gray-600">
					Wizard status could not be loaded.
				</p>
			</div>
		)
	}

	const meta = STEP_META[currentStep.stepId]
	const canAccessStep =
		currentStep.access === 'both' || isConsultant === true
	const isLocked = currentStep.status === 'locked'
	const isOptional = Boolean(meta.optional)

	const renderStepContent = () => {
		if (!currentStepId) return null;

		const props: WizardComponentProps = {
			isEmbedded: true,
			onFailure: handleChildFailure
		};

		switch (currentStepId) {
			case 'welcome':
				return (
					<div className="prose max-w-none">
						<h3>Welcome to the Migration Wizard</h3>
						<p>This wizard will guide you through the process of migrating data to SuccessFactors.</p>
						<ul>
							<li>Select a customer and phase (already done).</li>
							<li>Configure credentials.</li>
							<li>Map your data.</li>
							<li>Validate and export.</li>
						</ul>
					</div>
				);
			case 'customer':
				return (
					<div className="bg-gray-50 p-4 rounded-md">
						<p><strong>Current Customer ID:</strong> {customerId}</p>
						<p className="text-sm text-gray-600 mt-2">To change the customer, return to the <Link to="/customers" className="text-blue-600 underline">Customers list</Link>.</p>
					</div>
				);
			case 'phase':
				return (
					<div className="bg-gray-50 p-4 rounded-md">
						<p><strong>Current Phase ID:</strong> {phaseId}</p>
						<p className="text-sm text-gray-600 mt-2">To change the phase, return to the <Link to={`/customers/${customerId}/phases`} className="text-blue-600 underline">Phase list</Link>.</p>
					</div>
				);
			case 'credentials':
				return <CredentialForm {...props} />;
			case 'sharepoint':
				return <CustomerSharePointCredentialsForm customerId={customerId!} {...props} />;
			case 'files':
				return <FileUploader {...props} />;
			case 'metadata':
				return <MetadataViewer {...props} />;
			case 'entities':
				return <PhaseEntities {...props} />;
			case 'mapping':
				return <MappingCanvasV3 {...props} />;
			case 'validation':
				return <ValidationPanelV3 {...props} />;
			case 'export':
				return <ExportConfiguration {...props} />;
			case 'review':
				return (
					<div className="prose max-w-none">
						<h3>Migration Review</h3>
						<p>Review the completion status of all steps in the sidebar.</p>
						<p>If all required steps are marked complete, the migration for this phase is ready.</p>
					</div>
				);
			default:
				return <div>Step content not found.</div>;
		}
	};

	return (
		<div className="max-w-[95%] mx-auto p-6">
			{/* Breadcrumb Navigation */}
			<nav className="mb-4 flex items-center text-sm text-gray-600">
				<Link to="/customers" className="hover:text-primary-600 transition-colors">
					Customers
				</Link>
				<span className="mx-2">/</span>
				<Link
					to={`/customers/${customerId}/phases`}
					className="hover:text-primary-600 transition-colors"
				>
					Phases
				</Link>
				<span className="mx-2">/</span>
				<Link
					to={`/customers/${customerId}/phases/${phaseId}`}
					className="hover:text-primary-600 transition-colors"
				>
					Phase Dashboard
				</Link>
				<span className="mx-2">/</span>
				<span className="text-gray-900 font-medium">Wizard</span>
			</nav>

			{/* Back Button */}
			<Link
				to={`/customers/${customerId}/phases/${phaseId}`}
				className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
			>
				<ChevronLeftIcon className="w-4 h-4 mr-1" />
				Back to Phase Dashboard
			</Link>

			<div className="mb-6">
				<h1 className="text-3xl font-bold text-gray-900">
					Migration Wizard
				</h1>
				<p className="text-gray-600 mt-2">
					Follow the guided steps to complete the migration.
				</p>
			</div>

			<div className={layoutClassName}>
				<div className="space-y-3">
					<div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
						<p className="text-sm text-gray-600 font-medium">
							Progress: {completionStats.weightedPercent}% ({completionStats.completed}/{completionStats.total} visible steps)
						</p>
						<div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
							<div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${completionStats.weightedPercent}%` }}></div>
						</div>
					</div>
					<div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-1">
						{stepList.map((step) => (
							<WizardStepItem
								key={step.stepId}
								step={step}
								isSelected={step.stepId === currentStepId}
								onSelect={handleSelectStep}
							/>
						))}
					</div>
				</div>

				<div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm min-h-[600px] flex flex-col">
					<div className="flex items-start justify-between border-b pb-4 mb-6">
						<div>
							<h2 className="text-2xl font-semibold text-gray-900">
								{currentStep.label}
							</h2>
							<p className="text-gray-600 mt-1">{meta.description}</p>
						</div>
						<div className="flex flex-col items-end gap-2">
							<span className="text-xs uppercase tracking-wide text-gray-500 bg-gray-100 px-2 py-1 rounded">
								{currentStep.access === 'consultant'
									? 'Consultant only'
									: 'Shared step'}
							</span>
							<span className={`text-xs px-2 py-1 rounded font-medium ${currentStep.status === 'completed' ? 'text-green-700 bg-green-50' :
								currentStep.status === 'skipped' ? 'text-yellow-700 bg-yellow-50' :
									currentStep.status === 'locked' ? 'text-gray-500 bg-gray-100' :
										'text-blue-700 bg-blue-50'
								}`}>
								{currentStep.status.toUpperCase()}
							</span>
						</div>
					</div>

					<div className="flex-grow">
						{!canAccessStep ? (
							<div className={warningClassName}>
								You do not have access to complete this step.
							</div>
						) : isLocked ? (
							<div className={lockedClassName}>
								Complete previous steps to unlock this step.
							</div>
						) : (
							renderStepContent()
						)}
					</div>

					{/* Wizard Navigation Footer */}
					<div className="mt-8 pt-6 border-t flex flex-wrap items-center justify-between gap-4">
						<button
							type="button"
							onClick={handlePreviousStep}
							disabled={stepList.findIndex(s => s.stepId === currentStepId) <= 0}
							className="btn-secondary"
						>
							Previous Step
						</button>

						<div className="flex flex-wrap items-center gap-3">
							<button
								type="button"
								onClick={handleValidateStep}
								disabled={!canAccessStep || isLocked || validateMutation.isPending}
								className="btn-secondary"
							>
								{validateMutation.isPending ? 'Validating...' : 'Validate Status'}
							</button>

							{isOptional && (
								<button
									type="button"
									onClick={handleSkipStep}
									disabled={!canAccessStep || isLocked || completeMutation.isPending}
									className="btn-secondary"
								>
									Skip Step
								</button>
							)}

							<button
								type="button"
								onClick={handleCompleteStep}
								disabled={!canAccessStep || isLocked || completeMutation.isPending}
								className="btn-primary"
							>
								{completeMutation.isPending ? 'Updating...' : 'Mark as Complete'}
							</button>

							<button
								type="button"
								onClick={handleNextStep}
								disabled={stepList.findIndex(s => s.stepId === currentStepId) >= stepList.length - 1}
								className="btn-secondary"
							>
								Next Step
							</button>
						</div>
					</div>

					{/* Status Messages */}
					{(currentStep.validationError || (currentStep.validationWarnings && currentStep.validationWarnings.length > 0)) && (
						<div className="mt-4 p-4 bg-gray-50 rounded-md text-sm">
							{currentStep.validationError && (
								<p className="text-red-600 font-medium mb-1">
									Error: {currentStep.validationError}
								</p>
							)}
							{currentStep.validationWarnings &&
								currentStep.validationWarnings.length > 0 && (
									<ul className="list-disc ml-5 text-yellow-700">
										{currentStep.validationWarnings.map(
											(warning) => (
												<li key={warning}>{warning}</li>
											)
										)}
									</ul>
								)}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default WizardPage
